import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { formatJalaliDateTime } from "../utils/dateFormat";
const DetailRow = ({ label, value }) => (
  <>
    <Grid item xs={5}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Grid>
    <Grid item xs={7}>
      <Typography variant="body2">{value ?? "-"}</Typography>
    </Grid>
  </>
);

const AccountTransactionView = ({ open, onClose, data }) => {
  const { t } = useTranslation();

  const typeLabel =
    data?.transaction_type === "credit"
      ? t("accountTransaction.credit")
      : t("accountTransaction.debit");

  const formatDateTime = (value) => {
    if (!value) return "-";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return value;
    return formatJalaliDateTime(dt);
  };

  const printRows = [
    [t("accountTransaction.id"), data?.id],
    [t("accountTransaction.account"), data?.account_name || "-"],
    [t("accountTransaction.type"), typeLabel],
    [t("accountTransaction.amount"), data?.amount ?? "-"],
    [t("accountTransaction.transactionCurrency"), data?.transaction_currency_title || "-"],
    [t("accountTransaction.exchangeRate"), data?.exchange_rate ?? "-"],
    [t("accountTransaction.convertedAmount"), data?.converted_amount ?? "-"],
    [t("accountTransaction.accountCurrency"), data?.account_currency_title || "-"],
    [t("accountTransaction.transactionDate"), formatDateTime(data?.transaction_date)],
    [t("accountTransaction.description"), data?.description || "-"],
    [t("accountTransaction.shop", "Branch"), data?.shop_name || "-"],
    [t("accountTransaction.createdAt", "Created At"), formatDateTime(data?.created_at)],
  ];

  const handlePrint = () => {
    if (!data) return;
    const rowsHtml = printRows
      .map(
        ([label, value]) =>
          `<tr><td class="label">${label}</td><td>${value ?? "-"}</td></tr>`
      )
      .join("");

    const html = `
      <html>
        <head>
          <title>${t("accountTransaction.printTitle")} #${data.id}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 24px; color: #111827; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
            .title { margin: 0; font-size: 22px; font-weight: 700; }
            .subtitle { margin-top: 4px; color: #6b7280; font-size: 12px; }
            .badge {
              display: inline-block;
              padding: 4px 10px;
              border-radius: 999px;
              font-weight: 700;
              font-size: 12px;
              color: #fff;
              background: ${data?.transaction_type === "credit" ? "#16a34a" : "#dc2626"};
            }
            .summary {
              margin: 14px 0 18px;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 12px;
              background: #f9fafb;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 10px;
            }
            .summary-item {
              border: 1px solid #e5e7eb;
              border-radius: 6px;
              padding: 10px;
              background: #fff;
            }
            .summary-item .k { font-size: 11px; color: #6b7280; margin-bottom: 4px; }
            .summary-item .v { font-size: 17px; font-weight: 700; }
            table { width: 100%; border-collapse: collapse; border: 1px solid #e5e7eb; }
            td { border: 1px solid #e5e7eb; padding: 8px 10px; vertical-align: top; }
            td.label { width: 34%; font-weight: 600; background: #f3f4f6; }
            .footer { margin-top: 16px; font-size: 11px; color: #6b7280; text-align: right; }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h2 class="title">${t("accountTransaction.detailsTitle")}</h2>
              <div class="subtitle">${t("accountTransaction.id")}: #${data.id}</div>
            </div>
            <span class="badge">${typeLabel}</span>
          </div>
          <div class="summary">
            <div class="summary-grid">
              <div class="summary-item">
                <div class="k">${t("accountTransaction.convertedAmount")}</div>
                <div class="v">${data?.converted_amount ?? "-"} ${data?.account_currency_title || ""}</div>
              </div>
              <div class="summary-item">
                <div class="k">${t("accountTransaction.amount")}</div>
                <div class="v">${data?.amount ?? "-"} ${data?.transaction_currency_title || ""}</div>
              </div>
              <div class="summary-item">
                <div class="k">${t("accountTransaction.exchangeRate")}</div>
                <div class="v">${data?.exchange_rate ?? "-"}</div>
              </div>
            </div>
          </div>
          <table>
            ${rowsHtml}
          </table>
          <div class="footer">${t("accountTransaction.printedOn", "Printed on")}: ${formatJalaliDateTime()}</div>
        <div style="margin-top:12px;text-align:center;font-size:11px;color:#6b7280;">Powered by Afghan Cosmos IT Solutions | www.afghancosmos.com</div></body>
      </html>
    `;
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("accountTransaction.detailsTitle")}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Grid container rowSpacing={1.25}>
            <DetailRow label={t("accountTransaction.id")} value={data?.id} />
            <DetailRow label={t("accountTransaction.account")} value={data?.account_name} />
            <DetailRow label={t("accountTransaction.type")} value={typeLabel} />
            <DetailRow label={t("accountTransaction.amount")} value={data?.amount} />
            <DetailRow
              label={t("accountTransaction.transactionCurrency")}
              value={data?.transaction_currency_title}
            />
            <DetailRow label={t("accountTransaction.exchangeRate")} value={data?.exchange_rate} />
            <DetailRow
              label={t("accountTransaction.convertedAmount")}
              value={data?.converted_amount}
            />
            <DetailRow
              label={t("accountTransaction.accountCurrency")}
              value={data?.account_currency_title}
            />
            <DetailRow
              label={t("accountTransaction.transactionDate")}
              value={formatDateTime(data?.transaction_date)}
            />
            <DetailRow label={t("accountTransaction.description")} value={data?.description} />
            <DetailRow label={t("accountTransaction.shop", "Branch")} value={data?.shop_name} />
            <DetailRow
              label={t("accountTransaction.createdAt", "Created At")}
              value={formatDateTime(data?.created_at)}
            />
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("accountTransaction.close")}</Button>
        <Button variant="contained" onClick={handlePrint}>
          {t("accountTransaction.print")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountTransactionView;

